<template>
    <form v-on:submit.prevent="saveForm" novalidate>
        <XIcon
            class="h-8 w-8 text-blue-900 hover:text-gray-400 absolute cursor-pointer right-0 top-0 m-4 p-4 -mt-2 mr-3"
            @click="closeModal()"
        />

        <section :class="{ 'opacity-30': form.delete }">
            <label class="block text-sm font-medium text-gray-700 -mt-1">{{
                t("form-tenant.name", "Organisatie")
            }}</label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    type="text"
                    class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md cursor-not-allowed disabled:opacity-50"
                    :class="{
                        'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                            getError('organisation'),
                    }"
                    @keypress="errors = null"
                    v-model="form.organisation"
                    :readonly="!isSuperAdmin"
                    :disabled="!isSuperAdmin"
                />
            </div>

            <!-- EMAIL -->
            <label class="block text-sm font-medium text-gray-700 mt-2">{{
                t("form-tenant.email", "E-mailadres organisatie")
            }}</label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    type="email"
                    class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md"
                    :class="{
                        'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                            getError('email'),
                    }"
                    @keydown="errors = null"
                    v-model="form.email"
                    autocomplete="off"
                />
                <div
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        v-if="getError('email')"
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p class="mt-2 text-xs text-red-600" v-if="getError('email')">
                {{ getError("email", true) }}
            </p>
            <p class="mt-2 text-xs text-blue-600" v-if="!getError('email')">
                {{
                    t(
                        "form-tenant.email-placing",
                        "wordt vermeld in de uitnodigingen"
                    )
                }}
            </p>

            <!-- EMAIL -->
            <label class="block text-sm font-medium text-gray-700 mt-2">{{
                t("form-tenant.phone", "Telefoonnummer organisatie")
            }}</label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    type="tel"
                    class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md"
                    :class="{
                        'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                            getError('telephone'),
                    }"
                    no-keydown="errors = null"
                    v-model="form.telephone"
                />
                <div
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        v-if="getError('telephone')"
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p class="mt-2 text-xs text-red-600" v-if="getError('telephone')">
                {{ getError("telephone", true) }}
            </p>
            <p class="mt-2 text-xs text-blue-600" v-if="!getError('telephone')">
                {{
                    t(
                        "form-tenant.phone-placing",
                        "wordt vermeld in de uitnodigingen"
                    )
                }}
            </p>

            <!-- PHONE -->
            <label class="block text-sm font-medium text-gray-700 mt-2">{{
                t("form-tenant.address", "Adres")
            }}</label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <textarea
                    type="tel"
                    class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md"
                    :class="{
                        'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                            getError('address'),
                    }"
                    @keypress="errors = null"
                    v-model="form.address"
                />
                <div
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        v-if="getError('address')"
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p class="mt-2 text-xs text-red-600" v-if="getError('address')">
                {{ getError("address", true) }}
            </p>

            <!-- EMAIL -->
            <label class="block text-sm font-medium text-gray-700 mt-2">{{
                t("form-tenant.logo", "Logo")
            }}</label>
            <div class="inline">
                <img
                    :src="form.logo"
                    class="h-24 w-auto cursor-pointer hover:opacity-50 rounded-md shadow border border-gray-300 px-3 py-2 mt-1"
                    v-if="form.logo"
                    @click="triggerFileUpload('logo')"
                />

                <TrashIcon
                    v-if="form.logo"
                    @click="
                        form.reset_logo = true;
                        form.logo = null;
                    "
                    class="h-5 w-5 text-gray-500 inline float-right -mt-4"
                    aria-hidden="true"
                />
            </div>

            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    id="logo"
                    v-show="!form.logo"
                    ref="logo"
                    accept="image/*"
                    v-on:change="handleFileUpload('logo')"
                    type="file"
                    class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md"
                />
            </div>

            <SwitchGroup as="div" class="flex items-center mt-4">
                <Switch
                    v-model="form.poll_publish_directly"
                    :class="[
                        form.poll_publish_directly
                            ? 'bg-blue-600'
                            : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                    ]"
                >
                    <span
                        :class="[
                            form.poll_publish_directly
                                ? 'translate-x-5'
                                : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.poll_publish_directly
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.poll_publish_directly
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>
                <!-- 
                   'poll_publish_directly',
            'auto_record',
            'poll_anonymous',
            'show_voting_weight',
            'poll_publish_directly', -->

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t(
                            "form-tenant.settings.poll_publish_directly",
                            "Stemming direct publiceren"
                        )
                    }}</span>
                    <span class="text-xs text-blue-700 block hidden"
                        >Indien uitgevinkt moeten stemmingen handmatig worden
                        gepubliceerd</span
                    >
                </SwitchLabel>
            </SwitchGroup>

            <!-- OPTION -->
            <SwitchGroup as="div" class="flex items-center mt-3">
                <Switch
                    v-model="form.auto_record"
                    :class="[
                        form.auto_record ? 'bg-blue-600' : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                    ]"
                >
                    <span
                        :class="[
                            form.auto_record
                                ? 'translate-x-5'
                                : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.auto_record
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.auto_record
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>
                <!-- 
                   'poll_publish_directly',
            'auto_record',
            'poll_anonymous',
            'show_voting_weight',
            'poll_publish_directly', -->

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t(
                            "form-tenant.settings.auto_record",
                            "Vergaderingen opnemen"
                        )
                    }}</span>
                    <span class="text-xs text-blue-700 block hidden"
                        >Indien uitgevinkt worden vergaderingen niet
                        opgenomen</span
                    >
                </SwitchLabel>
            </SwitchGroup>

            <!-- OPTION -->
            <SwitchGroup as="div" class="flex items-center mt-3">
                <Switch
                    v-model="form.poll_anonymous"
                    :class="[
                        form.poll_anonymous ? 'bg-blue-600' : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                    ]"
                >
                    <span
                        :class="[
                            form.poll_anonymous
                                ? 'translate-x-5'
                                : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.poll_anonymous
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.poll_anonymous
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t(
                            "form-tenant.settings.poll_anonymous",
                            "Stemmingen niet anoniem"
                        )
                    }}</span>
                    <span class="text-xs text-blue-700 block hidden"
                        >Indien uitgevinkt zijn alle stemmingen anoniem</span
                    >
                </SwitchLabel>
            </SwitchGroup>

            <!-- OPTION -->
            <SwitchGroup as="div" class="flex items-center mt-3">
                <Switch
                    v-model="form.show_voting_weight"
                    :class="[
                        form.show_voting_weight ? 'bg-blue-600' : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                    ]"
                >
                    <span
                        :class="[
                            form.show_voting_weight
                                ? 'translate-x-5'
                                : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.show_voting_weight
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.show_voting_weight
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>
                <!-- 
                   'poll_publish_directly',
            'auto_record',
            'poll_anonymous',
            'show_voting_weight',
            'poll_publish_directly', -->

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t(
                            "form-tenant.settings.show_voting_weight",
                            "Stemgewicht tonen"
                        )
                    }}</span>
                    <span class="text-xs text-blue-700 block hidden"
                        >Indien uitgevinkt is het stemgewicht niet voor iedereen
                        zichtbaar</span
                    >
                </SwitchLabel>
            </SwitchGroup>

            <!-- OPTION -->
            <SwitchGroup as="div" class="flex items-center mt-3">
                <Switch
                    v-model="form.show_proxy_voting"
                    :class="[
                        form.show_proxy_voting ? 'bg-blue-600' : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                    ]"
                >
                    <span
                        :class="[
                            form.show_proxy_voting
                                ? 'translate-x-5'
                                : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.show_proxy_voting
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.show_proxy_voting
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>
                <!-- 
                   'poll_publish_directly',
            'auto_record',
            'poll_anonymous',
            'show_voting_weight',
            'poll_publish_directly', -->

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t(
                            "form-tenant.settings.show_proxy_voting",
                            "Volwachten toestaan"
                        )
                    }}</span>
                    <span class="text-xs text-blue-700 block hidden"
                        >Indien uitgevinkt is het stemgewicht niet voor iedereen
                        zichtbaar</span
                    >
                </SwitchLabel>
            </SwitchGroup>

            <!-- OPTION -->
            <SwitchGroup as="div" class="flex items-center mt-3">
                <Switch
                    v-model="form.locale_switcher"
                    :class="[
                        form.locale_switcher ? 'bg-blue-600' : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                    ]"
                >
                    <span
                        :class="[
                            form.locale_switcher
                                ? 'translate-x-5'
                                : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.locale_switcher
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.locale_switcher
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>
                <!-- 
                   'poll_publish_directly',
            'auto_record',
            'poll_anonymous',
            'show_voting_weight',
            'poll_publish_directly', -->

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t(
                            "form-tenant.settings.locale_switcher",
                            "Deelnemers kunnen taal wisselen"
                        )
                    }}</span>
                    <span
                        class="text-xs text-blue-700 block"
                        v-show="form.locale_switcher"
                        >{{
                            t(
                                "form-tenant.settings.locale_switcher_info",
                                "Indien aangevinkt kunnen gebruikers schakelen tussen de Duitse en Nederlandse taal"
                            )
                        }}</span
                    >
                </SwitchLabel>
            </SwitchGroup>

            <SwitchGroup
                as="div"
                class="flex items-center mt-4"
                v-if="form.has_iban"
                @click="checkDelete()"
            >
                <Switch
                    v-model="form.change_iban"
                    :class="[
                        form.change_iban ? 'bg-yellow-600' : 'bg-gray-200',
                        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500',
                    ]"
                >
                    <span
                        :class="[
                            form.change_iban
                                ? 'translate-x-5'
                                : 'translate-x-0',
                            'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                        ]"
                    >
                        <span
                            :class="[
                                form.change_iban
                                    ? 'opacity-0 ease-out duration-100'
                                    : 'opacity-100 ease-in duration-200',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-gray-400"
                                fill="none"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                        <span
                            :class="[
                                form.change_iban
                                    ? 'opacity-100 ease-in duration-200'
                                    : 'opacity-0 ease-out duration-100',
                                'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                            ]"
                            aria-hidden="true"
                        >
                            <svg
                                class="h-3 w-3 text-yellow-600"
                                fill="currentColor"
                                viewBox="0 0 12 12"
                            >
                                <path
                                    d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                />
                            </svg>
                        </span>
                    </span>
                </Switch>

                <SwitchLabel as="span" class="ml-3">
                    <span class="text-sm font-medium text-gray-900">{{
                        t(
                            "form-tenant.settings.change_iban",
                            "Rekeninggegevens aanpassen"
                        )
                    }}</span>
                    <span
                        class="text-xs text-yellow-700 block"
                        v-if="form.change_iban"
                        >{{
                            t(
                                "form-tenant.settings.confirm-overwrite-iban",
                                "huidige IBAN wordt overschreven"
                            )
                        }}</span
                    >
                </SwitchLabel>
            </SwitchGroup>

            <section
                v-show="form.change_iban"
                class="bg-gray-50 overflow-hidden shadow rounded-lg mt-4 pb-2"
            >
                <div class="px-5 py-2">
                    <label
                        class="block text-sm font-medium text-blue-700 mt-2"
                        v-show="form.change_iban"
                        >{{
                            t(
                                "form-tenant.change-iban-header-label",
                                "Rekeningnummer aanpassen"
                            )
                        }}</label
                    >
                    <label
                        class="block text-sm font-medium text-gray-700 mt-2"
                        >{{
                            t(
                                "form-tenant.iban-holder",
                                "Houder rekeningnummer"
                            )
                        }}</label
                    >
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            type="text"
                            class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md disabled:opacity-50"
                            :class="{
                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                    getError('holder'),
                            }"
                            @keypress="errors = null"
                            v-model="form.holder"
                        />
                    </div>
                    <p
                        class="mt-2 text-xs text-red-600"
                        v-if="getError('holder')"
                    >
                        {{
                            t(
                                "form-tenant.error-validation-holder",
                                "Houder van rekeningnummer dient ingevuld te zijn"
                            )
                        }}
                    </p>
                    <label
                        class="block text-sm font-medium text-gray-700 mt-2"
                        >{{ t("form-tenant.iban", "IBAN") }}</label
                    >
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            type="text"
                            class="block w-full pr-10 focus:outline-none sm:text-sm rounded-md disabled:opacity-50"
                            :class="{
                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                    getError('iban'),
                            }"
                            @keypress="errors = null"
                            v-model="form.iban"
                        />
                    </div>
                    <p
                        class="mt-2 text-xs text-red-600"
                        v-if="getError('iban')"
                    >
                        {{
                            t(
                                "form-tenant.error-validation-iban",
                                "Ingevoerde IBAN is niet valide, "
                            )
                        }}
                        {{ getError("iban", true) }}
                    </p>
                </div>
            </section>

            <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mt-2">{{
                    t("form-tenant.member-type", "Gebruikerslijst")
                }}</label>

                <fieldset class="my-1 mb-3">
                    <div class="space-y-2">
                        <div class="flex items-center">
                            <input
                                id="email"
                                name="notification-method"
                                type="radio"
                                value="deelnemers"
                                v-model="form.members_type"
                                class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                            />
                            <label
                                for="email"
                                class="ml-3 block text-sm font-medium text-gray-700"
                            >
                                {{
                                    t(
                                        "form-tenant.member-type-deelnemers",
                                        "Deelnemerslijst"
                                    )
                                }}
                            </label>
                        </div>

                        <div class="flex items-center">
                            <input
                                id="sms"
                                name="notification-method"
                                type="radio"
                                value="leden"
                                v-model="form.members_type"
                                class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                            />
                            <label
                                for="sms"
                                class="ml-3 block text-sm font-medium text-gray-700"
                            >
                                {{
                                    t(
                                        "form-tenant.member-type-leden",
                                        "Ledenadministratie"
                                    )
                                }}
                                <span
                                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                                >
                                    Bèta
                                </span>
                            </label>
                        </div>
                    </div>
                </fieldset>
            </div>

            <section
                v-show="isSuperAdmin"
                class="bg-gray-50 overflow-hidden shadow rounded-lg mt-4 pb-2"
            >
                <div class="px-5 py-2">
                    <label
                        class="block text-sm font-medium text-blue-700 mt-0"
                        v-show="isSuperAdmin"
                        >{{
                            t(
                                "form-tenant.superadmin-label",
                                "SuperAdmin instellingen"
                            )
                        }}</label
                    >
                    <!-- OPTION -->
                    <SwitchGroup
                        as="div"
                        class="flex items-center mt-3"
                        v-show="isSuperAdmin"
                    >
                        <Switch
                            v-model="form.show_embed"
                            :class="[
                                form.show_embed ? 'bg-blue-600' : 'bg-gray-200',
                                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                            ]"
                        >
                            <span
                                :class="[
                                    form.show_embed
                                        ? 'translate-x-5'
                                        : 'translate-x-0',
                                    'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                ]"
                            >
                                <span
                                    :class="[
                                        form.show_embed
                                            ? 'opacity-0 ease-out duration-100'
                                            : 'opacity-100 ease-in duration-200',
                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                    ]"
                                    aria-hidden="true"
                                >
                                    <svg
                                        class="h-3 w-3 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 12 12"
                                    >
                                        <path
                                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </span>
                                <span
                                    :class="[
                                        form.show_embed
                                            ? 'opacity-100 ease-in duration-200'
                                            : 'opacity-0 ease-out duration-100',
                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                    ]"
                                    aria-hidden="true"
                                >
                                    <svg
                                        class="h-3 w-3 text-blue-600"
                                        fill="currentColor"
                                        viewBox="0 0 12 12"
                                    >
                                        <path
                                            d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                        />
                                    </svg>
                                </span>
                            </span>
                        </Switch>
                        <!-- 
                   'poll_publish_directly',
            'auto_record',
            'poll_anonymous',
            'show_voting_weight',
            'poll_publish_directly', -->

                        <SwitchLabel as="span" class="ml-3">
                            <span class="text-sm font-medium text-gray-900">{{
                                t(
                                    "form-tenant.settings.show_embed",
                                    "Embed-code toestaan "
                                )
                            }}</span>
                            <span class="text-xs text-gray-700 block hidden"
                                >Indien aangevinkt kan een embed-code worden
                                gekoppeld aan een vergadering</span
                            >
                        </SwitchLabel>
                    </SwitchGroup>

                    <!-- OPTION -->
                    <SwitchGroup
                        as="div"
                        class="flex items-center mt-3"
                        v-show="isSuperAdmin"
                    >
                        <Switch
                            v-model="form.has_2fa"
                            :class="[
                                form.has_2fa ? 'bg-blue-600' : 'bg-gray-200',
                                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                            ]"
                        >
                            <span
                                :class="[
                                    form.has_2fa
                                        ? 'translate-x-5'
                                        : 'translate-x-0',
                                    'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                ]"
                            >
                                <span
                                    :class="[
                                        form.has_2fa
                                            ? 'opacity-0 ease-out duration-100'
                                            : 'opacity-100 ease-in duration-200',
                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                    ]"
                                    aria-hidden="true"
                                >
                                    <svg
                                        class="h-3 w-3 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 12 12"
                                    >
                                        <path
                                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </span>
                                <span
                                    :class="[
                                        form.has_2fa
                                            ? 'opacity-100 ease-in duration-200'
                                            : 'opacity-0 ease-out duration-100',
                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                    ]"
                                    aria-hidden="true"
                                >
                                    <svg
                                        class="h-3 w-3 text-blue-600"
                                        fill="currentColor"
                                        viewBox="0 0 12 12"
                                    >
                                        <path
                                            d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                        />
                                    </svg>
                                </span>
                            </span>
                        </Switch>
                        <!-- 
                   'poll_publish_directly',
            'auto_record',
            'poll_anonymous',
            'show_voting_weight',
            'poll_publish_directly', -->

                        <SwitchLabel as="span" class="ml-3">
                            <span class="text-sm font-medium text-gray-900">{{
                                t(
                                    "form-tenant.settings.show_has_2fa",
                                    "Two Factor Authentication"
                                )
                            }}</span>
                            <span class="text-xs text-gray-700 block hidden"
                                >Indien aangevinkt kan een embed-code worden
                                gekoppeld aan een vergadering</span
                            >
                        </SwitchLabel>
                    </SwitchGroup>
                </div>
            </section>
        </section>

        <div class="mt-3 sm:mt-6">
            <button
                type="submit"
                class="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50"
                :class="{
                    'bg-red-600 text-red-50 hover:bg-red-700': form.change_iban,
                    'bg-blue-600 text-blue-50 hover:bg-blue-700':
                        !form.change_iban,
                }"
                :disabled="loading"
            >
                <svg
                    v-if="loading"
                    class="animate-spin h-5 w-5 mr-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>
                {{ t("save", "Opslaan") }}
            </button>
        </div>
    </form>
</template>

<script>
import { ExclamationCircleIcon, XIcon, TrashIcon } from "@heroicons/vue/solid";
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
export default {
    components: {
        ExclamationCircleIcon,
        Switch,
        TrashIcon,
        SwitchGroup,
        SwitchLabel,
        XIcon,
        // VueMaskDirective,
    },
    mounted() {
        if (this.edit) {
            this.form = this.edit;
        }

        try {
            if (
                document.head.querySelector('meta[name="is_superadmin"]')
                    .content == "true"
            ) {
                this.isSuperAdmin = true;
            }
        } catch (error) {}
    },
    created() {},
    props: ["edit"],
    data() {
        return {
            form: {
                form_date: null,
                title: "Vergadering",
                time: null,
            },
            loading: false,
            errors: null,
            form_error: null,
            isSuperAdmin: false,
        };
    },
    methods: {
        closeModal() {
            Eventing.fire("closeModal");
        },
        getError(error, text) {
            try {
                if (this.errors) {
                    let errors = Object.values(this.errors);
                    if (text) {
                        return this.errors[error].toString();
                    }
                    return this.errors[error].toString() ? true : false;
                }
            } catch (error) {
                return false;
            }
            return false;
        },
        triggerFileUpload(field) {
            document.getElementById(field).click();
        },
        saveForm() {
            let self = this;
            this.loading = true;
            var url = "/api/organisation";
            var method = "post";

            axios({
                method: method,
                url: url,
                data: this.form,
            })
                .then((response) => {
                    this.loading = false;
                    this.$router.push({ name: "profile-index" });
                    Eventing.fire("notify", {
                        title: self.t("form-tenant.saved-title", "Gelukt"),
                        message: self.t(
                            "form-tenant.saved-text",
                            "Instellingen opgeslagen"
                        ),
                        type: "success",
                    });
                    if (self.form.reset_logo) {
                        Eventing.fire("setLogo", null);
                    }
                })
                .catch((error) => {
                    if (error.response) {
                        if (error.response.status == 404) {
                            this.$router.push("/login");
                        }
                        if (error.response.status == 422) {
                            self.form_error = "date";
                            this.errors = error.response.data.errors;
                        }
                        if (error.response.status == 409) {
                            this.loading = false;
                            this.$router.push({ name: "profile-index" });
                            Eventing.fire("notify", {
                                title: "IBAN",
                                message: self.t(
                                    "form-tenant.sync-iban-failure",
                                    "IBAN kon niet worden aangepast. Probeer het opnieuw."
                                ),
                                type: "error",
                            });
                        }
                    }
                    this.loading = false;
                });
        },
        handleFileUpload(field) {
            this.file = this.$refs.logo.files[0];
            var self = this;
            let formData = new FormData();
            // formData.append("bestand", this.file);
            formData.append("logo", this.file);
            formData.append("logo-upload", true);
            console.info(this.file);
            axios
                .post("/api/organisation", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then(function (response) {
                    console.info(response.data);
                    Eventing.fire("notify", {
                        title: self.t("form-tenant.saved-title", "Gelukt"),
                        message: self.t(
                            "form-tenant.saved-logo",
                            "Logo opgeslagen"
                        ),
                        type: "success",
                    });
                    self.form.logo = response.data;
                    Eventing.fire("setLogo", response.data);
                })
                .catch((error) => {
                    console.error(error);
                    if (error.response) {
                    }
                })
                .finally(function () {});
        },

        checkDelete() {
            var val = this.form.change_iban;
            if (val == true) {
                Eventing.fire("warning");
            } else {
                Eventing.fire("noWarning");
            }
        },
    },
};
</script>
